import React from 'react';
import {
    Box,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';

const PRIMARY = '#f25d27';

function DefaultMobileCard({ row, columns, onClick }) {
    const visibleColumns = columns.slice(0, 4);
    const primary = visibleColumns[0];
    const secondary = visibleColumns.slice(1);

    return (
        <Paper
            elevation={0}
            onClick={onClick}
            sx={{
                border: '1px solid #ececec',
                borderRadius: '10px',
                p: 1.4,
                boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                cursor: onClick ? 'pointer' : 'default',
                '&:hover': onClick ? { borderColor: PRIMARY } : {},
            }}
        >
            <Typography sx={{ fontSize: '0.88rem', fontWeight: 700, color: '#222', mb: 0.8 }}>
                {primary?.render ? primary.render(row) : row[primary?.field]}
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.6 }}>
                {secondary.map((column) => (
                    <Box key={column.id} sx={{ display: 'flex', gap: 0.6, alignItems: 'flex-start' }}>
                        <Typography sx={{ minWidth: 88, fontSize: '0.72rem', color: '#888', flexShrink: 0 }}>
                            {column.label}
                        </Typography>
                        <Typography sx={{ fontSize: '0.76rem', color: '#444', minWidth: 0 }}>
                            {column.render ? column.render(row) : row[column.field] ?? '—'}
                        </Typography>
                    </Box>
                ))}
            </Box>
        </Paper>
    );
}

export default function AdminDataTable({
    columns,
    rows,
    orderBy,
    orderDirection,
    onSort,
    onRowClick,
    mobileCardRenderer = null,
    mobileBreakpoint = 'sm',
    emptyMessage = 'Nenhum registro encontrado.',
}) {
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down(mobileBreakpoint));

    if (isSmallScreen && mobileCardRenderer) {
        if (rows.length === 0) {
            return (
                <Box sx={{ p: 3.5, textAlign: 'center' }}>
                    <Typography sx={{ color: '#888', fontSize: '0.86rem' }}>
                        {emptyMessage}
                    </Typography>
                </Box>
            );
        }

        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {rows.map((row, index) => (
                    <React.Fragment key={row.id !== undefined ? row.id : index}>
                        {mobileCardRenderer(row, index)}
                    </React.Fragment>
                ))}
            </Box>
        );
    }

    if (isSmallScreen && !mobileCardRenderer) {
        if (rows.length === 0) {
            return (
                <Box sx={{ p: 3.5, textAlign: 'center' }}>
                    <Typography sx={{ color: '#888', fontSize: '0.86rem' }}>
                        {emptyMessage}
                    </Typography>
                </Box>
            );
        }

        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {rows.map((row, index) => (
                    <DefaultMobileCard
                        key={row.id !== undefined ? row.id : index}
                        row={row}
                        columns={columns}
                        onClick={onRowClick ? () => onRowClick(row) : undefined}
                    />
                ))}
            </Box>
        );
    }

    return (
        <TableContainer sx={{ overflowX: 'auto' }}>
            <Table stickyHeader aria-label="tabela admin" sx={{ minWidth: 720 }}>
                <TableHead>
                    <TableRow>
                        {columns.map((column) => (
                            <TableCell
                                key={column.id}
                                align={column.align || 'left'}
                                aria-label={!column.label ? column.headerAriaLabel : undefined}
                                sx={{
                                    minWidth: column.minWidth,
                                    fontSize: isSmallScreen ? '0.8rem' : '0.9rem',
                                    fontWeight: 600,
                                    backgroundColor: '#fff',
                                }}
                                sortDirection={orderBy === column.field ? orderDirection : false}
                            >
                                {column.sortable !== false ? (
                                    <TableSortLabel
                                        active={orderBy === column.field}
                                        direction={orderBy === column.field ? orderDirection : 'asc'}
                                        onClick={() => onSort(column.field)}
                                        sx={{
                                            '&.Mui-active': { color: '#f25d27' },
                                            '& .MuiTableSortLabel-icon': {
                                                color: '#f25d27 !important',
                                            },
                                        }}
                                    >
                                        {column.label}
                                    </TableSortLabel>
                                ) : (
                                    column.label
                                )}
                            </TableCell>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {rows.length === 0 ? (
                        <TableRow>
                            <TableCell
                                colSpan={columns.length}
                                align="center"
                                sx={{ py: 6, color: '#666', fontSize: '0.95rem' }}
                            >
                                {emptyMessage}
                            </TableCell>
                        </TableRow>
                    ) : (
                        rows.map((row, index) => (
                            <TableRow
                                hover
                                key={row.id !== undefined ? row.id : index}
                                sx={{
                                    height: isSmallScreen ? 40 : 52,
                                    cursor: onRowClick ? 'pointer' : 'default',
                                }}
                                onClick={() => onRowClick && onRowClick(row)}
                            >
                                {columns.map((column) => (
                                    <TableCell
                                        key={column.id}
                                        align={column.align || 'left'}
                                        sx={{ fontSize: isSmallScreen ? '0.8rem' : '0.9rem' }}
                                    >
                                        {column.render ? column.render(row) : row[column.field]}
                                    </TableCell>
                                ))}
                            </TableRow>
                        ))
                    )}
                </TableBody>
            </Table>
        </TableContainer>
    );
}
